/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import CampaignModel from 'cio/models/campaign';
import { action } from '@ember/object';
import { VWBTab } from 'cio/utils/visual-workflow-builder/types';
import { Periods } from 'cio/utils/metrics/common';
import { tracked } from '@glimmer/tracking';
import { htmlSafe } from '@ember/template';
import { TimeRange } from 'cio/utils/time';
import { AddActionsPanelItemConfigs } from 'cio/utils/visual-workflow-builder/add-actions-panel';

interface VisualWorkflowBuilderAddActionsPanelArgs {
  campaign: CampaignModel;
  isSaving: boolean;
  tab: VWBTab;
  changeTab: (tab: VWBTab) => unknown;
  metricsTabSelectedMeasures: string[];
  selectMetricsTabMeasure: (measure: string) => unknown;
  deselectMetricsTabMeasure: (measure: string) => unknown;
  metricsTabTimePeriod: Periods;
  metricsTabTimeRange: TimeRange;
  changeMetricsTabTimePeriod: (period: Periods) => unknown;
  changeMetricsTabTimeRange: (range: TimeRange) => unknown;
  shouldShowCopyFrom: boolean;
  startCopyFrom: () => unknown;
  itemConfigs?: AddActionsPanelItemConfigs;
  hasReadOnlyPermission: boolean;
}

const BOTTOM_SCROLL_DETECT_MARGIN = 20;

export default class VisualWorkflowBuilderAddActionsPanel extends Component<VisualWorkflowBuilderAddActionsPanelArgs> {
  panelBodyElement: HTMLElement | null = null;
  resizeObserver: ResizeObserver | null = null;

  @tracked hasScrollbar = false;
  @tracked scrollbarWidth = 0;
  @tracked isScrolledToBottom = false;

  @action
  didInsert(element: HTMLElement) {
    this.panelBodyElement = element;

    this.resizeObserver = new ResizeObserver(() => requestAnimationFrame(this.onResize));
    this.resizeObserver.observe(this.panelBodyElement);

    this.addScrollListener();
  }

  willDestroy() {
    super.willDestroy();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    this.removeScrollListener();
  }

  @action
  addScrollListener() {
    if (!this.panelBodyElement) {
      return;
    }

    this.panelBodyElement.addEventListener('scroll', this.onScroll);
  }

  @action
  removeScrollListener() {
    if (!this.panelBodyElement) {
      return;
    }

    this.panelBodyElement.removeEventListener('scroll', this.onScroll);
  }

  @action
  onScroll() {
    window.requestAnimationFrame(this.onScrollAnimationFrame);
  }

  @action
  onScrollAnimationFrame() {
    if (!this.panelBodyElement) {
      return;
    }

    // https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight#determine_if_an_element_has_been_totally_scrolled
    // Check whether the container has been scrolled to the bottom, with a small
    // margin.
    this.isScrolledToBottom =
      this.panelBodyElement.scrollHeight - this.panelBodyElement.scrollTop <=
      this.panelBodyElement.clientHeight + BOTTOM_SCROLL_DETECT_MARGIN;
  }

  @action
  onResize() {
    if (!this.panelBodyElement) {
      return;
    }

    this.hasScrollbar =
      this.panelBodyElement.scrollHeight > this.panelBodyElement.clientHeight + BOTTOM_SCROLL_DETECT_MARGIN;
    this.scrollbarWidth = this.panelBodyElement.offsetWidth - this.panelBodyElement.clientWidth;
  }

  get scrollIndicatorStyle() {
    return htmlSafe(`--scrollbarWidth: ${this.scrollbarWidth}px;`);
  }
}
